// @ts-nocheck
/**
 * Boat overlay for the native-GL explore/fleet map — a Canvas2D layer (NOT a Mapbox symbol layer).
 *
 * WHY CANVAS, NOT A SYMBOL LAYER: a Mapbox `symbol` layer carrying our SDF boat glyph crashes Mapbox's
 * incremental symbol placement (`continuePlacement` → undefined layout) as soon as a `line` layer (the
 * track) coexists — reproduced deterministically in the GL harness. The live map already renders boats on
 * a Canvas2D overlay over GL line tracks with no such issue, so we use the same proven approach here.
 *
 * The canvas is `pointerEvents:none` (never blocks map pan); picking is done by hit-testing the click point
 * against the last-drawn boat screen positions via a map 'click' handler. Position is interpolated to the
 * exact playhead (matches the track head); heading from the nearest sample; halo pulses on manual seek.
 */

import { createEffect, onCleanup, onMount } from 'solid-js';
import {
  selectedTime,
  timeWindow,
  isPlaying,
  playbackSpeed,
  isUnsetPlaybackTime,
  isManualTimeChange,
} from '../../../../../store/playbackStore';
import { selectedRange, selectedRanges } from '../../../../../store/selectionStore';
import { sourcesStore } from '../../../../../store/sourcesStore';
import { persistantStore } from '../../../../../store/persistantStore';
import { defaultChannelsStore } from '../../../../../store/defaultChannelsStore';
import { resolveBoatMarkerStyle } from '../../renderers/boatMarkerStyle';
import { drawWindArrowOnCanvas } from '../../renderers/windArrowGeometry';

// Glyphs (bow at +y); rotate(hdg+180-bearing) points the bow to heading — same as the live/SVG layers.
const MONOHULL_PATH: Array<[number, number]> = [
  [0, -12], [-4, -12], [-4, 0], [-2, 8], [0, 12], [2, 8], [4, 0], [4, -12],
];
const MULTIHULL_PATH: Array<[number, number]> = [
  [0, 12], [5.332, 0], [5.332, 6], [6.665, 12], [8, 6], [8, -12],
  [-8, -12], [-8, 6], [-6.665, 12], [-5.332, 6], [-5.332, 0],
];

export interface GlMapBoatLayerProps {
  map: any;
  data: any[];
  samplingFrequency: number;
  selectedSourceIds?: Set<number>;
  fallbackSourceId?: number;
  onBoatClick?: (point: any) => void;
  onBoatHover?: (sourceId: number | null) => void;
  highlightedSourceIds?: Set<number>;
  effectivePlaybackTime?: Date | null;
  /** Averaged wind per source id; present only while the wind overlay wants boat wind drawn. */
  boatWindBySourceId?: Map<number, { tws: number; twd: number }>;
}

export default function GlMapBoatLayer(props: GlMapBoatLayerProps) {
  let canvas: HTMLCanvasElement | null = null;
  const lastHdg = new Map<number, number>();
  // Last drawn boat screen positions (for click/hover hit-testing) + the source point (for onBoatClick).
  let hitTargets: Array<{ sid: number; x: number; y: number; pt: any }> = [];

  const sourceKey = (d: any) => d?.source_id ?? d?.Source_id ?? d?.sourceId ?? d?.sourceID;
  const { latName, lngName, hdgName } = defaultChannelsStore;
  const num = (v: any) => {
    if (v == null) return undefined;
    const n = Number(v);
    return isNaN(n) ? undefined : n;
  };
  const getLat = (d: any) => num(d?.[latName()] ?? d?.Lat ?? d?.lat);
  const getLng = (d: any) => num(d?.[lngName()] ?? d?.Lng ?? d?.lng);
  const getHdg = (d: any) => {
    const f = hdgName();
    return num(d?.[f] ?? d?.[f.toLowerCase()] ?? d?.[f.toUpperCase()] ?? d?.Hdg ?? d?.hdg);
  };
  const getTs = (d: any): number => {
    const t = d?.Datetime ?? d?.timestamp ?? d?.time ?? d?.datetime;
    if (!t) return 0;
    if (t instanceof Date) return t.getTime();
    const p = new Date(t).getTime();
    return isNaN(p) ? 0 : p;
  };

  const container = (): HTMLElement | null => {
    try {
      return props.map?.getCanvasContainer?.() || null;
    } catch {
      return null;
    }
  };

  const sizeCanvas = () => {
    const map = props.map;
    if (!canvas || !map?.getCanvas) return false;
    const mc = map.getCanvas();
    const w = mc.clientWidth || mc.width || 0;
    const h = mc.clientHeight || mc.height || 0;
    if (w <= 0 || h <= 0) return false;
    const dpr = window.devicePixelRatio || 1;
    const cw = Math.round(w * dpr);
    const ch = Math.round(h * dpr);
    if (canvas.width !== cw || canvas.height !== ch) {
      canvas.width = cw;
      canvas.height = ch;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }
    return true;
  };

  const boatScale = (): number => {
    const map = props.map;
    if (!map?.getZoom) return 1;
    const zoom = map.getZoom();
    const lat = map.getCenter ? map.getCenter().lat : 0;
    const mpp = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
    const targetPx = 22 / mpp;
    let scale = Math.min(12, Math.max(0.75, targetPx / 24));
    const iconType = String(persistantStore.selectedClassIcon?.() || 'monohull').toLowerCase();
    if (iconType === 'multihull') scale *= 0.75;
    return scale;
  };
  const glyph = () =>
    String(persistantStore.selectedClassIcon?.() || 'monohull').toLowerCase() === 'multihull'
      ? MULTIHULL_PATH
      : MONOHULL_PATH;

  const brushRanges = (): any[] => {
    const r = selectedRanges();
    const s = selectedRange();
    if (Array.isArray(r) && r.length > 0) return r;
    if (Array.isArray(s) && s.length > 0) return s;
    return [];
  };
  const inBrush = (d: any, ranges: any[]) => {
    if (ranges.length === 0) return true;
    const t = getTs(d);
    return ranges.some((r) => {
      const s = r.start_time instanceof Date ? r.start_time.getTime() : new Date(r.start_time).getTime();
      const e = r.end_time instanceof Date ? r.end_time.getTime() : new Date(r.end_time).getTime();
      return t >= s && t <= e;
    });
  };

  // One O(n) pass: nearest sample (for tolerance + heading) and bracket lerp (for exact position).
  const sample = (data: any[], tMs: number) => {
    let best: any = null;
    let bestD = Infinity;
    let prev: any = null;
    let next: any = null;
    let prevTs = -Infinity;
    let nextTs = Infinity;
    for (const d of data) {
      const ts = getTs(d);
      const dd = Math.abs(ts - tMs);
      if (dd < bestD) {
        bestD = dd;
        best = d;
      }
      if (ts <= tMs && ts > prevTs) {
        prev = d;
        prevTs = ts;
      }
      if (ts >= tMs && ts < nextTs) {
        next = d;
        nextTs = ts;
      }
    }
    if (!best) return null;
    let lng = getLng(best);
    let lat = getLat(best);
    if (prev && next && prevTs < nextTs) {
      const la1 = getLat(prev);
      const ln1 = getLng(prev);
      const la2 = getLat(next);
      const ln2 = getLng(next);
      if (la1 !== undefined && ln1 !== undefined && la2 !== undefined && ln2 !== undefined) {
        const f = (tMs - prevTs) / (nextTs - prevTs);
        lat = la1 + (la2 - la1) * f;
        lng = ln1 + (ln2 - ln1) * f;
      }
    }
    return { pt: best, delta: bestD, lng, lat, hdg: getHdg(best) };
  };

  const groupsAt = () => {
    const raw = Array.isArray(props.data) ? props.data : [];
    const enabled = props.selectedSourceIds;
    const ranges = brushRanges();
    const groups = new Map<number, any[]>();
    const hasSourceIds = raw.some((d) => sourceKey(d) != null);
    for (const d of raw) {
      if (!inBrush(d, ranges)) continue;
      let sid: number;
      if (hasSourceIds) {
        sid = Number(sourceKey(d));
        if (!Number.isFinite(sid)) continue;
        if (enabled && enabled.size > 0 && !enabled.has(sid)) continue;
      } else {
        sid = Number(props.fallbackSourceId ?? persistantStore.selectedSourceId?.() ?? 0);
      }
      if (!groups.has(sid)) groups.set(sid, []);
      groups.get(sid)!.push(d);
    }
    return { groups, hasSourceIds };
  };

  const colorName = (sid: number, hasSourceIds: boolean) => {
    const info = sourcesStore.sources().find((s: any) => Number(s.source_id) === sid);
    const name = info?.source_name || (hasSourceIds ? `Source ${sid}` : persistantStore.selectedSourceName?.() || '');
    const color = info?.color || (name && sourcesStore.getSourceColor(name)) || '#1f77b4';
    return { name, color };
  };

  // Seek-halo state (manual seek): hold then fade; animated by a short rAF loop since the map may be idle.
  let haloStart = 0;
  const HALO_HOLD = 1400;
  const HALO_FADE = 700;
  let haloRaf: any = null;
  const haloOpacity = () => {
    if (!haloStart) return 0;
    const dt = performance.now() - haloStart;
    if (dt < HALO_HOLD) return 0.5;
    if (dt < HALO_HOLD + HALO_FADE) return 0.5 * (1 - (dt - HALO_HOLD) / HALO_FADE);
    return 0;
  };
  const pulseHalo = () => {
    haloStart = performance.now();
    if (haloRaf) cancelAnimationFrame(haloRaf);
    const step = () => {
      draw();
      if (haloOpacity() > 0) haloRaf = requestAnimationFrame(step);
      else {
        haloRaf = null;
        haloStart = 0;
        draw();
      }
    };
    haloRaf = requestAnimationFrame(step);
  };

  const draw = () => {
    const map = props.map;
    if (!canvas || !map) return;
    sizeCanvas();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    hitTargets = [];

    const t = props.effectivePlaybackTime ?? selectedTime();
    if (!t || isUnsetPlaybackTime(t)) return;
    const tMs = t.getTime();

    const { groups, hasSourceIds } = groupsAt();
    if (groups.size === 0) return;

    const sps = props.samplingFrequency && Number.isFinite(props.samplingFrequency) ? props.samplingFrequency : 1;
    const baseTol = Math.max(500, Math.round(1000 / Math.max(0.001, sps)) * 3);
    const tol = isPlaying() ? baseTol * 2 : baseTol;

    const bearing = map.getBearing ? map.getBearing() : 0;
    const scale = boatScale();
    const path = glyph();
    const hOpacity = haloOpacity();
    const highlighted = props.highlightedSourceIds ?? new Set<number>();

    for (const [sid, data] of groups.entries()) {
      const s = sample(data, tMs);
      if (!s || s.delta > tol || s.lng === undefined || s.lat === undefined) continue;
      const p = map.project([s.lng, s.lat]);
      if (!p || Number.isNaN(p.x) || Number.isNaN(p.y)) continue;
      const { name, color } = colorName(sid, hasSourceIds);

      // Halo (under the glyph) on manual seek
      if (hOpacity > 0) {
        ctx.save();
        ctx.globalAlpha = hOpacity;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 22, 0, Math.PI * 2);
        ctx.fillStyle = resolveBoatMarkerStyle(name, color, color).fill;
        ctx.fill();
        ctx.restore();
      }

      // Rotation: hdg + 180 - bearing (bow to heading, rotates with the map)
      let rot = lastHdg.get(sid) ?? 0;
      if (s.hdg != null && Number.isFinite(s.hdg)) {
        rot = s.hdg + 180 - bearing;
        lastHdg.set(sid, s.hdg);
      }
      const isHi = highlighted.has(sid);
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate((rot * Math.PI) / 180);
      ctx.scale(scale, scale);
      ctx.beginPath();
      ctx.moveTo(path[0][0], path[0][1]);
      for (let i = 1; i < path.length; i++) ctx.lineTo(path[i][0], path[i][1]);
      ctx.closePath();
      const boatMarkerStyle = resolveBoatMarkerStyle(name, color, isHi ? '#fff' : '#000');
      ctx.fillStyle = boatMarkerStyle.fill;
      ctx.strokeStyle = boatMarkerStyle.stroke;
      ctx.lineWidth = isHi ? 2 : 1;
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      // Label (unrotated, to the right)
      ctx.save();
      ctx.translate(p.x + 12 * Math.max(1, scale * 0.5), p.y);
      ctx.font = '11px sans-serif';
      ctx.textBaseline = 'middle';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.fillStyle = '#fff';
      ctx.strokeText(name, 0, 0);
      ctx.fillText(name, 0, 0);
      ctx.restore();

      // Wind arrow + TWS label, same convention as the marks (points downwind, locked to the map bearing).
      const wind = props.boatWindBySourceId?.get(sid);
      if (wind && Number.isFinite(wind.twd) && Number.isFinite(wind.tws)) {
        drawWindArrowOnCanvas(
          ctx,
          { x: p.x, y: p.y, twd: wind.twd, mapBearing: bearing },
          { color: boatMarkerStyle.fill, label: wind.tws.toFixed(1) }
        );
      }

      hitTargets.push({ sid, x: p.x, y: p.y, pt: { ...s.pt, source_id: sid } });
    }
    // Dev/test hook (gated): expose drawn boat screen positions for numeric alignment checks.
    if (typeof window !== 'undefined' && (window as any).__glExposeBoat) {
      (window as any).__glBoatTargets = hitTargets.map((h) => ({ sid: h.sid, x: h.x, y: h.y }));
    }
  };

  // ── Picking via map events (canvas is pointerEvents:none) ──
  const HIT_R = 14;
  const pick = (e: any) => {
    const pt = e.point;
    let best: any = null;
    let bestD = HIT_R * HIT_R;
    for (const h of hitTargets) {
      const dx = h.x - pt.x;
      const dy = h.y - pt.y;
      const d = dx * dx + dy * dy;
      if (d <= bestD) {
        bestD = d;
        best = h;
      }
    }
    return best;
  };
  const onClick = (e: any) => {
    const h = pick(e);
    if (h && props.onBoatClick) props.onBoatClick(h.pt);
  };
  const onMove = (e: any) => {
    // NOTE: don't set map cursor here — this is a GLOBAL mousemove (canvas has pointerEvents:none) and would
    // clobber the track layer's own pointer cursor. Boats sit on the track, so the track's cursor covers them.
    const h = pick(e);
    if (props.onBoatHover) props.onBoatHover(h ? h.sid : null);
  };

  onMount(() => {
    const el = container();
    if (!el) return;
    canvas = document.createElement('canvas');
    canvas.className = 'gl-map-boat-overlay';
    Object.assign(canvas.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '1000',
    });
    el.appendChild(canvas);
    sizeCanvas();

    const onRender = () => draw();
    const onResize = () => {
      sizeCanvas();
      draw();
    };
    props.map?.on?.('render', onRender);
    props.map?.on?.('move', onRender);
    props.map?.on?.('resize', onResize);
    props.map?.on?.('click', onClick);
    props.map?.on?.('mousemove', onMove);
    window.addEventListener('resize', onResize);

    onCleanup(() => {
      props.map?.off?.('render', onRender);
      props.map?.off?.('move', onRender);
      props.map?.off?.('resize', onResize);
      props.map?.off?.('click', onClick);
      props.map?.off?.('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      if (haloRaf) cancelAnimationFrame(haloRaf);
      if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
      canvas = null;
    });
  });

  // Recompute + redraw on the playhead / data / selection changes.
  createEffect(() => {
    void props.data;
    void props.selectedSourceIds;
    void props.highlightedSourceIds;
    void props.effectivePlaybackTime;
    void props.boatWindBySourceId;
    void selectedTime();
    void timeWindow();
    void isPlaying();
    void playbackSpeed();
    void selectedRange();
    void selectedRanges();
    draw();
  });

  // Halo pulse on manual seek.
  createEffect(() => {
    const t = selectedTime();
    const manual = isManualTimeChange();
    if (t && manual && !isPlaying()) pulseHalo();
  });

  return null;
}
